import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Modal from "../../components/Modal";
import Loader from "../../components/Loader";
import TextDiv from "../../components/TextDiv";
import CustomInput from "../../components/CustomInput";
import CustomButton from "../../components/CustomButton";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import ErrorBox from "../../components/ErrorBox";
import HotDropdown from "../../components/HotDropdown";

// Services
import { createStudentTransaction } from "../../services/schoolTransactionsServices";
import { getAllKassas } from "../../services/kassa/kassaServices";

// Utils
import { formatDate } from "../../utils/toDate";

// Assets
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import BalanceBoxes from "../../components/BalanceBoxes";
import { getStudentYears } from "../../services/StudentServices";
import { getPaymentCode } from "../../services/PaymentCodeService";
import PaymentReceipt from "./StudentPaymentTemplate";
import { useReactToPrint } from "react-to-print";
import useLanguage from "../../utils/useLanguage";

const ReturnMoneyModal = ({
  title = "returnMoney",
  selectedStudent,
  handleToggleReturnMoneyModal,
  modalSize,
  refetchData,
  setShouldGetStudents,
}) => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const receiptRef = useRef(null);

  // Form state
  const [inputs, setInputs] = useState({
    amount: 0,
    notes: "",
    date: new Date(),
  });

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Data states
  const [studentYears, setStudentYears] = useState([]);
  const [selectedStudentYear, setSelectedStudentYear] = useState(null);
  const [currentYearData, setCurrentYearData] = useState(null);
  const [receiptNumber, setReceiptNumber] = useState(null);

  // Kassa states
  const [kassas, setKassas] = useState([]);
  const [selectedKassa, setSelectedKassa] = useState(null);
  const [kassasLoading, setKassasLoading] = useState(false);

  // Transform student years data for dropdown
  const transformStudentYearsForDropdown = (years) => {
    const yearsArr = years.map((year) => ({
      title: `${year.schoolClass.name} ${
        year.active ? `${t("active")}` : `${t("inactive")}`
      }`,
      value: year.id,
      yearData: year,
    }));
    return yearsArr;
  };

  // Transform kassas data for dropdown
  const transformKassasForDropdown = (kassasData) => {
    return kassasData.map((kassa) => ({
      title: kassa.name,
      value: kassa.id,
      kassaData: kassa,
    }));
  };

  const fetchStudentAcademicYears = async () => {
    try {
      const response = await getStudentYears(selectedStudent.id);

      if (response.status === 200) {
        setStudentYears(response.data);

        // Find and set the active year as default, or first year if no active year
        const activeYear = response.data.find((year) => year.active);
        const defaultYear = activeYear || response[0];

        setCurrentYearData(defaultYear);
        setSelectedStudentYear({
          title: `${defaultYear.schoolClass.name} ${
            defaultYear.active ? `${t("active")}` : `${t("inactive")}`
          }`,
          value: defaultYear.id,
          yearData: defaultYear,
        });
      }
    } catch (error) {
      console.error("Error fetching student years:", error);
    }
  };

  const getReceiptNumber = async () => {
    try {
      const response = await getPaymentCode();
      if (response.status == 200) {
        setReceiptNumber(response?.data?.code);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch Kassas
  const fetchKassas = async () => {
    setKassasLoading(true);
    try {
      const response = await getAllKassas({
        page: 1,
        limit: 100,
      });

      if (response.status === 200) {
        setKassas(response.data.kassas);

        // Set first kassa as default if available
        if (response.data.kassas.length > 0) {
          const firstKassa = response.data.kassas[0];
          setSelectedKassa({
            title: firstKassa.name,
            value: firstKassa.id,
            kassaData: firstKassa,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching Kassas:", error);
      setError("failedToFetchKassas");
    } finally {
      setKassasLoading(false);
    }
  };

  // Handle dropdown selection change
  const handleYearSelection = (selectedOption) => {
    // Find the full year data from studentYears array
    const yearData = studentYears.find(
      (year) => year.id === selectedOption.value
    );

    setSelectedStudentYear(selectedOption);
    setCurrentYearData(yearData);
  };

  // Handle kassa selection
  const handleKassaSelection = (selectedOption) => {
    setSelectedKassa(selectedOption);

    // Clear error if it was related to kassa selection
    if (error === "pleaseSelectKassa") {
      setError("");
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "date") {
      setInputs((prev) => ({ ...prev, [name]: new Date(value) }));
    } else {
      setInputs((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (error) {
      setError("");
    }
  };

  // Validate form inputs
  const validateInputs = () => {
    if (!inputs.amount || inputs.amount <= 0) {
      setError("pleaseEnterValidAmount");
      return false;
    }

    if (!selectedStudent?.id) {
      setError("studentNotSelected");
      return false;
    }

    if (!currentYearData) {
      setError("pleaseSelectAcademicYear");
      return false;
    }

    if (!selectedKassa) {
      setError("pleaseSelectKassa");
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSave = async () => {
    if (!validateInputs()) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      // the transaction type present until we add category id
      const transactionData = {
        date: inputs.date.toISOString(),
        notes: inputs.notes,
        studentYearId: currentYearData.id,
        studentId: selectedStudent.id,
        amount: inputs.amount,
        transactionType: "reverseTransaction",
        schoolClassId: currentYearData.studentClassId,
        kassaId: selectedKassa.value, // Add kassaId to the transaction data
        studentCategoryId: selectedStudent.studentCategoryId,
        receiptNumber: receiptNumber || null,
        type: "reverseTransaction",
      };

      const response = await createStudentTransaction(transactionData);

      if (response.status === 201) {
        // Success - close modal and refresh data
        handleToggleReturnMoneyModal();

        if (refetchData) {
          refetchData();
        }

        if (setShouldGetStudents) {
          setShouldGetStudents(true);
        }

        handlePrint();
      }
    } catch (error) {
      console.error("Error creating return money transaction:", error);

      // Handle different error types
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.response?.status === 400) {
        setError("invalidTransactionData");
      } else if (error.response?.status === 404) {
        setError("studentOrClassNotFound");
      } else {
        setError("transactionCreationFailed");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setError("");
    handleToggleReturnMoneyModal();
  };

  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: `${t("returnMoneyReceipt")}-${selectedStudent?.fullName}-${
      selectedStudent?.studentCode
    }-${formatDate(new Date())}`,
    pageStyle: `
    @page {
      margin: 0 !important;
      size: A4;
    }
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        direction: ${currentDirection};
        width: 100% !important;
        height: 100% !important;
      }
      body {
        -webkit-print-color-adjust: exact;
        direction: ${currentDirection};
        margin: 0 !important;
        padding: 0 !important;
      }
      .printing {
        display: flex !important;
      }
      .no-print {
        display: flex !important;
      }
    }
  `,
  });

  // Initialize component data
  useEffect(() => {
    if (selectedStudent) {
      fetchStudentAcademicYears();
      fetchKassas();
      getReceiptNumber();
    }
  }, [selectedStudent]);

  return (
    <Modal
      title={t(title)}
      onClose={handleClose}
      modalSize={modalSize}
      footer={
        <>
          {loading && <Loader />}
          <section className="o-row o-row--gap-size_300 o-row--horizontal-align_end">
            <CustomButton
              title={t("cancel")}
              onClick={handleClose}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
            <CustomButton
              title={t("saveAndPrint")}
              onClick={handleSave}
              icon={saveIcon}
              iconSize={16}
              disabled={loading}
            />
          </section>
        </>
      }
    >
      <div className="o-column o-column--gap-size_300">
        {error && <ErrorBox>{t(error)}</ErrorBox>}

        {/* Student Information Display */}
        <div className="o-column o-column--gap-size_200">
          <TextDiv
            title="student"
            value={selectedStudent?.fullName}
            divClasses="o-row o-row--gap-size_300 o-row--vertical-align_center"
            pClasses="u-min-width-specific_150"
          />
          <TextDiv
            title="studentId"
            value={selectedStudent?.studentCode}
            divClasses="o-row o-row--gap-size_300 o-row--vertical-align_center"
            pClasses="u-min-width-specific_150"
          />

          <TextDiv
            title="returnReceiptCode"
            value={receiptNumber}
            divClasses="o-row o-row--gap-size_300 o-row--vertical-align_center u-margin--top_400"
            pClasses="u-min-width-specific_150"
          />

          <div className="o-row o-row--vertical-align_center">
            <label className="u-min-width-specific_150">
              {t("academicLevel")}:
            </label>
            {studentYears.length !== 0 ? (
              <HotDropdown
                initOptions={transformStudentYearsForDropdown(studentYears)}
                selected={selectedStudentYear?.title || ""}
                setSelected={handleYearSelection}
                optionsName="studentYears"
                thick
                flex
              />
            ) : (
              <Loader size="small" />
            )}
          </div>
        </div>

        {/* Transaction Form */}
        <div className="o-column o-column--gap-size_300">
          {/* Date Input */}
          <div className="o-row o-row--vertical-align_center">
            <label htmlFor="dateInput" className="u-min-width-specific_150">
              {t("returnDate")}:
            </label>
            <CustomInput
              name="date"
              value={formatDate(inputs.date)}
              type="date"
              onChange={handleInputChange}
              flex
              id="dateInput"
            />
          </div>

          {/* Amount Input */}
          <div className="o-row o-row--vertical-align_center">
            <label htmlFor="amountInput" className="u-min-width-specific_150">
              {t("returnAmount")}:
            </label>
            <CustomInputWithUnit
              name="amount"
              value={inputs.amount}
              type="moneyV2"
              onChange={handleInputChange}
              id="amountInput"
              flex
              unit="IQD"
            />
          </div>

          {/* Kassa Selection */}
          <div className="o-row o-row--vertical-align_center">
            <label className="u-min-width-specific_150">{t("kassa")}:</label>
            {kassasLoading ? (
              <Loader size="small" />
            ) : kassas.length > 0 ? (
              <HotDropdown
                initOptions={transformKassasForDropdown(kassas)}
                selected={selectedKassa?.title || ""}
                setSelected={handleKassaSelection}
                optionsName="kassas"
                thick
                flex
              />
            ) : (
              <span className="u-color_error">{t("noKassasAvailable")}</span>
            )}
          </div>

          {/* Notes Input */}
          <div className="o-row o-row--vertical-align_start">
            <label htmlFor="notesInput" className="u-min-width-specific_150">
              {t("returnReason")}:
            </label>
            <CustomInput
              name="notes"
              value={inputs.notes}
              onChange={handleInputChange}
              id="notesInput"
              type="textarea"
              flex
            />
          </div>
        </div>

        <BalanceBoxes
          boxes={[
            {
              title: "balance",
              value: selectedStudentYear?.yearData.balance || t("none"),
              currency: selectedStudentYear?.yearData.balance && "IQD",
            },
          ]}
        />
      </div>

      {/* TEMPLATE */}
      <div>
        <div ref={receiptRef}>
          <PaymentReceipt
            receiptRef={receiptRef}
            student={selectedStudent}
            amount={inputs.amount}
            date={inputs.date}
            receiptNumber={receiptNumber}
            paymentFor="Money Return"
            paymentMethod="Cash"
            notes={inputs.notes}
            kassa={selectedKassa?.title}
            academicYear={selectedStudentYear?.title}
            isReturnTransaction={true}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ReturnMoneyModal;
